// @ts-check
import { forwardRef, useImperativeHandle, useState, useEffect, useCallback, useMemo, useRef } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { useSelector } from "react-redux"
import { AnimatePresence, motion, useReducedMotion } from "framer-motion"
import CanvasToolbar from "./CanvasToolbar"
import CanvasEditor from "./CanvasEditor"
import DiffOverlay from "./DiffOverlay"
import ReviewBar from "./ReviewBar"
import { findDiffMatches } from "./diffMarkers"
import { mergeDraftedEdits, useEditPlayback } from "./useEditPlayback"
import { ReviewProgressLine } from "../chat-bubble/ReviewProgressLine"
import { useReviewProgress } from "@/services/chat-provider/canvas/reviewProgress"
import { useActiveCanvasDocumentId, useDocument, useUpdateDocument, useAcceptDiff, useRejectDiff, serializeCanvasDocuments, DocumentQueryKeys } from "@/services/chat-provider/canvas"
import { useChatContext, AgentMode } from "@/services/chat-provider"
import { OPEN_CANVAS_PANEL_EVENT } from "@/services/chat-provider/canvas/panelEvents"
// DEV ONLY — remove before shipping
import { MOCK_DOCUMENTS } from "./__dev__/mockDocuments"

// The stream handler also opens the panel, so the event name lives with the chat provider.
export { OPEN_CANVAS_PANEL_EVENT }

const SCAN_STAGES = ["reading", "thinking", "searching", "drafting_edits"]
const NO_DRAFTS = []

/**
 * @typedef {{ openPanel: (documentId: string) => void, closePanel: () => void }} CanvasPanelComponent
 */

/**
 * @param {{ onOpenPanel: () => void, onClosePanel: () => void }} props
 * @param {React.Ref<CanvasPanelComponent>} ref
 */
const CanvasPanel = forwardRef(function CanvasPanel({ onOpenPanel, onClosePanel }, ref) {
  const [isOpen, setIsOpen] = useState(false)
  const [activeDocumentId, setActiveDocumentId] = useActiveCanvasDocumentId()
  const canvasEditorRef = useRef(null)
  const [listOpen, setListOpen] = useState(false)
  const [activeDiffId, setActiveDiffId] = useState(/** @type {string | null} */(null))

  // @ts-ignore
  const { useActiveChatSession, useSendAgentMessage, useActiveSessionHasActiveStream } = useChatContext()
  const { data: activeChatSession } = useActiveChatSession()
  const sessionId = activeChatSession?._id ?? activeChatSession?.id
  const { mutate: sendAgentMessage } = useSendAgentMessage()
  const isStreaming = useActiveSessionHasActiveStream()
  const queryClient = useQueryClient()
  const { user } = useSelector((/** @type {any} */ state) => state.app.datastate)

  const { data: fetchedDoc, isLoading: isFetching } = useDocument(activeDocumentId)
  const { mutate: updateDocument } = useUpdateDocument(activeDocumentId ?? "")
  const { mutate: acceptDiffMutate } = useAcceptDiff(activeDocumentId ?? "", sessionId)
  const { mutate: rejectDiffMutate } = useRejectDiff(activeDocumentId ?? "")

  // Live per-token polling while streaming was removed — it caused UI jitter
  // and raced with cache updates elsewhere. Instead, refetch once when the
  // agent's response finishes so the canvas catches up with the final diffs.
  const wasStreamingRef = useRef(isStreaming)
  useEffect(() => {
    if (wasStreamingRef.current && !isStreaming && activeDocumentId) {
      queryClient.invalidateQueries({ queryKey: DocumentQueryKeys.DOCUMENT(activeDocumentId) })
    }
    wasStreamingRef.current = isStreaming
  }, [isStreaming, activeDocumentId, queryClient])

  const reviewProgress = useReviewProgress(sessionId)
  const isLiveReview = isStreaming && reviewProgress != null
  const drafted = reviewProgress?.drafted ?? NO_DRAFTS

  // Show suggestions as soon as the agent has saved them, not at the end of the run.
  const savedEdits = reviewProgress?.saved
  useEffect(() => {
    if (savedEdits && activeDocumentId) {
      queryClient.invalidateQueries({ queryKey: DocumentQueryKeys.DOCUMENT(activeDocumentId) })
    }
  }, [savedEdits, activeDocumentId, queryClient])

  // DEV ONLY: use mock data when the doc ID matches a known dummy
  const mockDoc = import.meta.env.DEV ? (MOCK_DOCUMENTS[activeDocumentId] ?? null) : null
  const [mockDocState, setMockDocState] = useState(mockDoc)

  useEffect(() => {
    if (import.meta.env.DEV) setMockDocState(MOCK_DOCUMENTS[activeDocumentId] ?? null)
  }, [activeDocumentId])

  const canvasDoc = mockDocState ?? fetchedDoc
  const isLoading = mockDocState ? false : isFetching

  const currentContent = canvasDoc?.currentVersion?.content ?? ""

  // A chat can be shared, but only the owner can change its document.
  const isViewOnly = canvasDoc?.owner != null && canvasDoc.owner !== user?._id

  // Edits play into the document one at a time while a review runs. Saved
  // suggestions wait for playback, so the document fills in step by step.
  const revealedDrafts = useEditPlayback(drafted, currentContent, isLiveReview)
  const revealedCount = revealedDrafts.length
  const isPlayingEdits = revealedCount < drafted.length
  const isReviewing = isLiveReview || isPlayingEdits

  const pendingDiffs = useMemo(() => canvasDoc?.pendingDiffs ?? [], [canvasDoc?.pendingDiffs])
  const shownDiffs = useMemo(
    () => (isReviewing ? mergeDraftedEdits(pendingDiffs, drafted, revealedDrafts) : pendingDiffs),
    [isReviewing, pendingDiffs, drafted, revealedDrafts],
  )
  const newestDraft = isReviewing ? revealedDrafts.at(-1) : null
  const animateDiffId = newestDraft ? (newestDraft.edit.id || `draft-${newestDraft.number}`) : null

  // Document order first; suggestions with no card in the text come last.
  const { orderedDiffs, placedIds } = useMemo(() => {
    const placed = findDiffMatches(currentContent, shownDiffs).map(match => match.diff)
    const ids = new Set(placed.map(diff => diff.id))
    return { orderedDiffs: [...placed, ...shownDiffs.filter(diff => !ids.has(diff.id))], placedIds: ids }
  }, [currentContent, shownDiffs])

  // A double click must not send the same decision twice.
  const resolvingDiffIdsRef = useRef(new Set())
  /**
   * @param {string} diffId
   * @param {(variables: { diffId: string }, options: { onSettled: () => void }) => void} mutate
   */
  function resolveDiffOnce(diffId, mutate) {
    if (resolvingDiffIdsRef.current.has(diffId)) return
    resolvingDiffIdsRef.current.add(diffId)
    mutate({ diffId }, { onSettled: () => resolvingDiffIdsRef.current.delete(diffId) })
  }

  const pendingDiffCount = orderedDiffs.filter((d) => d.status === "pending").length
  const activeIndex = orderedDiffs.findIndex(diff => diff.id === activeDiffId)


  // Auto-open to the most recently modified canvas only on an actual session
  // switch (e.g. navigating into a session that already has reviews). A new
  // canvas appearing mid-conversation should NOT auto-open the panel — the
  // user opens it deliberately via the "Click to see AI suggestions" button
  // in the chat bubble once the review finishes.
  const prevSessionIdRef = useRef(activeChatSession?._id)
  const keepOpenForSessionRef = useRef(/** @type {string | null} */(null))
  useEffect(() => {
    const sessionChanged = prevSessionIdRef.current !== activeChatSession?._id
    prevSessionIdRef.current = activeChatSession?._id
    if (!sessionChanged) return

    if (!activeChatSession?.canvases?.length) {
      // A review started in a new session opens its document before the session has loaded it.
      if (keepOpenForSessionRef.current !== activeChatSession?._id) closePanel()
      return
    }
    const canvases = activeChatSession.canvases
    const latest = canvases[canvases.length - 1]
    if (latest?.documentId) openPanel(latest.documentId)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeChatSession?._id])

  // Listen for "View in canvas" events from chat bubbles
  useEffect(() => {
    /** @param {CustomEvent} e */
    function handleEvent(e) {
      const { documentId, sessionId: openedForSession } = e.detail ?? {}
      keepOpenForSessionRef.current = openedForSession ?? null
      if (documentId) openPanel(documentId)
    }
    document.addEventListener(OPEN_CANVAS_PANEL_EVENT, handleEvent)
    return () => document.removeEventListener(OPEN_CANVAS_PANEL_EVENT, handleEvent)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const openPanel = useCallback((documentId) => {
    setActiveDocumentId(documentId)
    setIsOpen(true)
    onOpenPanel?.()
  }, [onOpenPanel])

  const closePanel = useCallback(() => {
    setIsOpen(false)
    setActiveDocumentId(null)
    onClosePanel?.()
  }, [onClosePanel, setActiveDocumentId])

  useImperativeHandle(ref, () => ({ openPanel, closePanel }))

  if (!isOpen) return null

  // ── Move between suggestions ─────────────────────────────────────────────
  /** @param {string} diffId */
  function selectDiff(diffId) {
    if (placedIds.has(diffId)) {
      setActiveDiffId(diffId)
      canvasEditorRef.current?.focusDiff(diffId)
      return
    }
    // No card in the text: its comparison opens in the list.
    setListOpen(true)
    setActiveDiffId(current => (current === diffId ? null : diffId))
  }

  /** @param {1 | -1} step */
  function moveToDiff(step) {
    const count = orderedDiffs.length
    if (count === 0) return
    const start = activeIndex === -1 ? (step === 1 ? -1 : 0) : activeIndex
    selectDiff(orderedDiffs[(start + step + count) % count].id)
  }

  // ── Save ───────────────────────────────────────────────────────────────────
  function handleSave(markdown) {
    if (!activeDocumentId) return
    if (mockDocState) {
      setMockDocState(prev => ({
        ...prev,
        currentVersion: { ...prev.currentVersion, content: markdown },
      }))
      return
    }
    updateDocument({ content: markdown, sessionId })
  }

  // ── Accept / Reject ────────────────────────────────────────────────────────
  const handleAcceptDiff = mockDocState
    ? (diffId) => {
        setMockDocState(prev => {
          const diff = prev.pendingDiffs.find(d => d.id === diffId)
          if (!diff) return prev
          const newContent = diff.isFullRewrite
            ? diff.new_string
            : (prev.currentVersion.content.includes(diff.old_string)
                ? prev.currentVersion.content.replace(diff.old_string, diff.new_string)
                : prev.currentVersion.content)
          return {
            ...prev,
            pendingDiffs: prev.pendingDiffs.filter(d => d.id !== diffId),
            currentVersion: { ...prev.currentVersion, content: newContent },
            canUndo: true,
          }
        })
      }
    : (diffId) => resolveDiffOnce(diffId, acceptDiffMutate)

  const handleRejectDiff = mockDocState
    ? (diffId) => setMockDocState(prev => ({
        ...prev,
        pendingDiffs: prev.pendingDiffs.filter(d => d.id !== diffId),
      }))
    : (diffId) => resolveDiffOnce(diffId, rejectDiffMutate)

  // ── Suggest edits for a selection ─────────────────────────────────────────
  function handleSuggestEdits(selectedText) {
    if (!selectedText || !activeDocumentId || !sessionId) return
    sendAgentMessage({
      sessionId,
      query: `Please suggest edits with tracked changes for just this section of the document:\n\n"""${selectedText}"""`,
      files: [],
      images: [],
      mode: AgentMode.CANVAS_AGENT,
      canvasDocuments: serializeCanvasDocuments(canvasDoc),
    })
  }

  // ── Mock toolbar handlers ─────────────────────────────────────────────────
  const mockToolbarHandlers = mockDocState ? {
    onAcceptAll: () => pendingDiffs
      .filter(d => d.status === "pending")
      .forEach(d => handleAcceptDiff(d.id)),
    onRejectAll: () => setMockDocState(prev => ({
      ...prev,
      pendingDiffs: prev.pendingDiffs.filter(d => d.status !== "pending"),
    })),
    onUndo: () => setMockDocState(prev => ({ ...prev, canUndo: false, canRedo: true })),
    onRedo: () => setMockDocState(prev => ({ ...prev, canUndo: true, canRedo: false })),
  } : undefined

  return (
    <div
      className="flex flex-col h-full overflow-hidden bg-background md:rounded-2xl md:border border-border md:m-2 md:h-[calc(100%-1rem)]"
      data-view-only={isViewOnly || undefined}
    >
      {isLoading ? (
        <CanvasSkeleton />
      ) : canvasDoc ? (
        <>
          <CanvasToolbar
            documentId={activeDocumentId}
            sessionId={sessionId}
            title={canvasDoc.title ?? "Untitled"}
            currentVersionId={canvasDoc.currentVersionId}
            updatedAt={canvasDoc.updatedAt}
            canUndo={canvasDoc.canUndo ?? false}
            canRedo={canvasDoc.canRedo ?? false}
            onClose={closePanel}
            mockHandlers={mockToolbarHandlers}
            readOnly={isViewOnly}
            onToggleHeading={(level) => canvasEditorRef.current?.toggleHeading(level)}
            onToggleBulletList={() => canvasEditorRef.current?.toggleBulletList()}
            onToggleOrderedList={() => canvasEditorRef.current?.toggleOrderedList()}
            onInsertInlineMath={() => canvasEditorRef.current?.insertInlineMath()}
            onInsertBlockMath={() => canvasEditorRef.current?.insertBlockMath()}
          />
          <AnimatePresence initial={false} mode="wait">
            {isReviewing && reviewProgress ? (
              <BarTransition key="progress">
                <ReviewProgressLine
                  progress={reviewProgress}
                  text={isPlayingEdits ? `Adding suggestion ${revealedCount} of ${drafted.length}` : undefined}
                  showSteps={false}
                  className="flex items-center h-11 px-3 sm:px-4 border-b border-border bg-muted/30 [&>div]:w-full"
                />
              </BarTransition>
            ) : orderedDiffs.length > 0 ? (
              <BarTransition key="review">
                <ReviewBar
                  documentId={activeDocumentId}
                  sessionId={sessionId}
                  suggestionCount={orderedDiffs.length}
                  pendingCount={pendingDiffCount}
                  position={activeIndex + 1}
                  onPrevious={() => moveToDiff(-1)}
                  onNext={() => moveToDiff(1)}
                  listOpen={listOpen}
                  onToggleList={() => setListOpen(open => !open)}
                  listOnlyCount={orderedDiffs.length - placedIds.size}
                  mockHandlers={mockToolbarHandlers}
                />
              </BarTransition>
            ) : null}
          </AnimatePresence>
          <div className="relative flex flex-col flex-1 min-h-0">
            <CanvasEditor
              ref={canvasEditorRef}
              content={currentContent}
              pendingDiffs={orderedDiffs}
              onSave={handleSave}
              onAcceptDiff={handleAcceptDiff}
              onRejectDiff={handleRejectDiff}
              onSuggestEdits={handleSuggestEdits}
              readOnly={isReviewing || isViewOnly}
              animateDiffId={animateDiffId}
            />
            <AnimatePresence>
              {isLiveReview && revealedCount === 0 && SCAN_STAGES.includes(reviewProgress.stage) && <ReviewScan key="review-scan" />}
            </AnimatePresence>
          </div>
          <AnimatePresence initial={false}>
            {listOpen && !isReviewing && orderedDiffs.length > 0 && (
              <DiffOverlay
                key="suggestions"
                diffs={orderedDiffs}
                placedIds={placedIds}
                activeDiffId={activeDiffId}
                onSelect={selectDiff}
                onAccept={handleAcceptDiff}
                onReject={handleRejectDiff}
                onClose={() => setListOpen(false)}
              />
            )}
          </AnimatePresence>
        </>
      ) : (
        <div className="flex items-center justify-center h-full text-sm text-muted-foreground">
          Document not found.
        </div>
      )}
    </div>
  )
})

/** @param {{ children: React.ReactNode }} props */
function BarTransition({ children }) {
  const reduceMotion = useReducedMotion()
  return (
    <motion.div
      initial={reduceMotion ? false : { opacity: 0, y: -6 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.16 }}
      className="shrink-0"
    >
      {children}
    </motion.div>
  )
}

/** Left, top, width and height of each blob, as a percentage of the cloud. */
const CLOUD_BLOBS = [
  [-6, 16, 58, 70],
  [22, 0, 62, 84],
  [52, 14, 56, 74],
  [8, 40, 50, 58],
  [42, 44, 54, 56],
  [70, 34, 40, 56],
]

/** One short, soft cloud that scans down the page while the model reads the document. */
function ReviewScan() {
  const reduceMotion = useReducedMotion()
  if (reduceMotion) return null
  return (
    <motion.div
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 overflow-hidden"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
    >
      <div className="review-cloud">
        {CLOUD_BLOBS.map(([left, top, width, height], index) => (
          <div
            key={left}
            className="review-cloud-blob"
            style={{
              left: `${left}%`,
              top: `${top}%`,
              width: `${width}%`,
              height: `${height}%`,
              animationDuration: `${3 + index * 0.6}s`,
              animationDelay: `${-index * 0.9}s`,
            }}
          />
        ))}
      </div>
    </motion.div>
  )
}

function CanvasSkeleton() {
  return (
    <div className="flex flex-col gap-3 p-4 animate-pulse">
      <div className="h-7 w-48 rounded bg-muted" />
      <div className="h-4 w-full rounded bg-muted" />
      <div className="h-4 w-5/6 rounded bg-muted" />
      <div className="h-4 w-4/6 rounded bg-muted" />
    </div>
  )
}

export default CanvasPanel
